import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import scmp, { FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';

const propTypes = {
  title: PropTypes.string,
  scheduleVisits: PropTypes.array,

  provider: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  claimAction: PropTypes.string.isRequired,
  submitVisits: PropTypes.func.isRequired,
};

const defaultProps = {
  title: '',
  scheduleVisits: [],
};

@autobind
class TableSubmittableVisits extends BaseComponent {
  constructor(props) {
    super(props);
  }

  onRowSelect(record, selected, selectedRows) {
  }

  onRowSelectAll(selected, selectedRows, changeRows) {
    changeRows.forEach((changeRow) => {
      this.onRowSelect(changeRow, selected, selectedRows);
    });
  }

  async onSubmitClick() {
    const { claimAction, submitVisits } = this.props;

    const selectedVisitIds = this.dataTables.getSelectedRowKeys();
    if (selectedVisitIds.length > 0) {
      submitVisits(selectedVisitIds, claimAction);
    } else {
      this.notifyWarning('No visit is selected.');
    }
  }

  rowClassName(record, index) {
    const selectedRowKeys = this.dataTables?.getSelectedRowKeys();
    return selectedRowKeys && selectedRowKeys.includes(record.id) ? 'highlight' : undefined;
  }

  createColumns() {
    const { provider } = this.props;

    const columns = [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
    }, {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      defaultSortOrder: 'ascend',
      render: (text, record) => (
        <scmp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.invoiceId}`}
          target="_blank"
        >
          {text}
        </scmp.A>
      ),
    }, {
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <scmp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </scmp.A>
      ),
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <scmp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </scmp.A>
      ),
    }, {
      title: 'Sch. Time',
      dataIndex: 'schTimeString',
      sorter: (a, b) => a.fromTime - b.fromTime,
    }, {
      title: 'Visit Time',
      dataIndex: 'visitInOutString',
      sorter: (a, b) => a.visitIn - b.visitIn,
    }, {
      title: 'Duration',
      dataIndex: 'visitDurationString',
      sorter: (a, b) => a.visitDurationString?.localeCompare(b.visitDurationString),
    }, {
      title: 'Confirm Time',
      dataIndex: 'confirmTimeUsString',
      sorter: (a, b) => a.confirmTime - b.confirmTime,
    }];

    // Specific columns for different providers.
    if (provider === 'careBridge') {
      columns.push({
        title: 'CareBridge Status',
        dataIndex: 'careBridgeStatus',
        sorter: (a, b) => a.careBridgeStatus?.localeCompare(b.careBridgeStatus),
        render: (text, record) => (
          <scmp.Span
            width="wrap_content"
            height="wrap_content"
            color={record.careBridgeStatus === 'FAILURE' ? 'red_light' : 'textPrimaryDark'}
            fontSize="12px"
          >
            {text}
          </scmp.Span>
        ),
      }, {
        title: 'Last Submit Error',
        dataIndex: 'careBridgeLastFailResponse',
        sorter: (a, b) => a.careBridgeLastFailResponse?.localeCompare(b.careBridgeLastFailResponse),
        render: (text, record) => record.careBridgeStatus === 'FAILURE' && (
          <Tooltip title={text}>
            <scmp.Span
              width="wrap_content"
              height="wrap_content"
              maxWidth="150px"
              color="textPrimaryDark"
              fontSize="12px"
              ellipsis
              display="inline-block"
            >
              {text}
            </scmp.Span>
          </Tooltip>
        ),
      });
    } else if (provider === 'hhaExchange') {
      columns.push({
        title: 'HHAExchange Status',
        dataIndex: 'hhaExchangeStatus',
        sorter: (a, b) => a.hhaExchangeStatus?.localeCompare(b.hhaExchangeStatus),
        render: (text, record) => (
          <scmp.Span
            width="wrap_content"
            height="wrap_content"
            color={record.hhaExchangeStatus === 'FAILURE' ? 'red_light' : 'textPrimaryDark'}
            fontSize="12px"
          >
            {text}
          </scmp.Span>
        ),
      }, {
        title: 'Last Submit Error',
        dataIndex: 'hhaExchangeLastFailResponse',
        sorter:
          (a, b) => a.hhaExchangeLastFailResponse?.localeCompare(b.hhaExchangeLastFailResponse),
        render: (text, record) => record.hhaExchangeStatus === 'FAILURE' && (
          <Tooltip title={text}>
            <scmp.Span
              width="wrap_content"
              height="wrap_content"
              maxWidth="150px"
              color="textPrimaryDark"
              fontSize="12px"
              ellipsis
              display="inline-block"
            >
              {text}
            </scmp.Span>
          </Tooltip>
        ),
      });
    }

    return columns;
  }

  createDataSource(scheduleVisits) {
    const dataSource = [];
    scheduleVisits.forEach((visit, index) => {
      dataSource.push({
        key: visit.getId(),
        id: visit.getId(),
        date: visit.getLong('date'),
        dateUsString: visit.getString('dateUsString'),
        companyName: visit.getString('companyName'),
        serviceCode: visit.getRemoteObject('service')?.getString('displayCode'),
        patientId: visit.getLong('patientId'),
        patientName: visit.getString('patientName'),
        caregiverId: visit.getLong('caregiverId'),
        caregiverName: visit.getString('caregiverName'),
        fromTime: visit.getLong('fromTime'),
        toTime: visit.getLong('toTime'),
        schTimeString: `${visit.getString('fromTimeString')} - ${visit.getString('toTimeString')}`,
        visitIn: visit.getLong('visitIn'),
        visitOut: visit.getLong('visitOut'),
        visitInOutString: `${visit.getString('visitInString')} - ${visit.getString('visitOutString')}`,
        visitDurationString: visit.getString('visitDurationString'),
        confirmTime: visit.getLong('confirmTime'),
        confirmTimeUsString: visit.getString('confirmTimeUsString'),
        invoiceId: visit.getLong('invoiceId'),
        invoiceNumber: visit.getString('invoiceNumber'),
        careBridgeStatus: visit.getString('careBridgeStatus'),
        careBridgeLastFailResponse: visit.getString('careBridgeLastFailResponse'),
        hhaExchangeStatus: visit.getString('hhaExchangeStatus'),
        hhaExchangeLastFailResponse: visit.getString('hhaExchangeLastFailResponse'),
      });
    });
    return dataSource;
  }

  renderFooter() {
    const { submitting, claimAction } = this.props;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        {claimAction === 'N' &&
          <CustomButton
            width="wrap_content"
            height="32px"
            backgroundColor="purple_wisteria"
            fontSize="14px"
            type="primary"
            loading={submitting}
            onClick={this.onSubmitClick}
          >
            SUBMIT NEW VISITS
          </CustomButton>
        }

        {claimAction === 'C' &&
          <CustomButton
            width="wrap_content"
            height="32px"
            backgroundColor="purple_wisteria"
            fontSize="14px"
            type="primary"
            loading={submitting}
            onClick={this.onSubmitClick}
          >
            SUBMIT CORRECTED VISITS
          </CustomButton>
        }
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { scheduleVisits, title, claimAction, ...layoutProps } = this.props;

    const rowSelection = {
      getCheckboxProps: record => ({
        disabled: !claimAction,
      }),
      onSelect: this.onRowSelect,
      onSelectAll: this.onRowSelectAll,
    };

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="purple_wisteria"
        fontSize="12px"
        size="small"
        title={title}
        icon="cloud-upload"
        withColumnFilter={false}
        selectable
        rowSelection={rowSelection}
        rowClassName={this.rowClassName}
        columns={this.createColumns()}
        dataSource={this.createDataSource(scheduleVisits)}
        footer={this.renderFooter()}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableSubmittableVisits.propTypes = propTypes;
TableSubmittableVisits.defaultProps = defaultProps;

export default TableSubmittableVisits;
